import { ReportLeadCard } from '../../shared/ReportLead';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
    alpha,
    Box,
    Card,
    CardContent,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    Typography,
    useTheme
} from '@mui/material';
import { NumericFormat } from 'react-number-format';
import { MdAnalytics } from 'react-icons/md';
import { AppVarsContext } from '../../../context/AppVarsContext';
import Comments from '../../comments';
import { GlassLoadingState, formatReportDateRangeLabel } from '../../shared/reportStatusCards';
import { formatUsd } from '../../../system/report-formatters';

const safeNum = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
};

const formatNumber = (value) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(safeNum(value));
const formatCurrency = (value) => formatUsd(safeNum(value));

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    if (value !== index) return null;

    return (
        <Box role="tabpanel" {...other}>
            {children}
        </Box>
    );
}

function a11yProps(index) {
    return {
        id: `virtuous-tab-${index}`,
        'aria-controls': `virtuous-tabpanel-${index}`,
    };
}

const VirtuousReport = (props) => {
    const theme = useTheme();
    const { appVars, appLoading } = useContext(AppVarsContext);
    const [value, setValue] = useState(0);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const reportId = sessionStorage.getItem('rid');
    const title = props?.title || 'Virtuous';
    const dateRangeLabel = formatReportDateRangeLabel(sessionStorage.getItem('sDate'), sessionStorage.getItem('eDate'));

    const fetchReportData = useCallback(async () => {
        if (!appVars?.api_url || !reportId) {
            setData([]);
            setLoading(false);
            props?.setLoading?.(false);
            return;
        }

        setLoading(true);
        props?.setLoading?.(true);

        try {
            const res = await fetch(`${appVars.api_url}/virtuous/email/stats`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    cid: reportId,
                    sdate: sessionStorage.getItem('sDate'),
                    edate: sessionStorage.getItem('eDate')
                })
            });
            const response = await res.json();
            const rows = Array.isArray(response) ? response : (Array.isArray(response?.data) ? response.data : []);
            setData(rows);
        } catch (error) {
            console.error('Error fetching Virtuous data:', error);
            setData([]);
        } finally {
            setLoading(false);
            props?.setLoading?.(false);
        }
    }, [appVars?.api_url, props, reportId]);

    useEffect(() => {
        if (!appLoading) {
            fetchReportData();
        }
    }, [appLoading, fetchReportData]);

    useEffect(() => {
        const handleStorageChange = () => {
            fetchReportData();
        };

        window.addEventListener('dc', handleStorageChange);
        return () => window.removeEventListener('dc', handleStorageChange);
    }, [fetchReportData]);

    const rows = useMemo(() => {
        const rawRows = Array.isArray(data) ? data : [];
        return rawRows.map((row, index) => ({
            id: row?.id || row?.pkRecID || `${title}-${index}`,
            emailName: row?.email_name || row?.EMAIL_NAME || `Email ${index + 1}`,
            delivered: safeNum(row?.delivered),
            opened: safeNum(row?.opened),
            clicked: safeNum(row?.clicked),
            bounced: safeNum(row?.bounced),
            unsubscribe: safeNum(row?.unsubscribe),
            gifts: safeNum(row?.gifts),
            revenue: safeNum(row?.revenue)
        }));
    }, [data, title]);

    const totals = useMemo(() => rows.reduce((acc, row) => {
        acc.delivered += row.delivered;
        acc.opened += row.opened;
        acc.clicked += row.clicked;
        acc.bounced += row.bounced;
        acc.unsubscribe += row.unsubscribe;
        acc.gifts += row.gifts;
        acc.revenue += row.revenue;
        return acc;
    }, {
        delivered: 0,
        opened: 0,
        clicked: 0,
        bounced: 0,
        unsubscribe: 0,
        gifts: 0,
        revenue: 0
    }), [rows]);

    const kpiItems = useMemo(() => ([
        { label: 'Emails', value: formatNumber(rows.length) },
        { label: 'Delivered', value: formatNumber(totals.delivered) },
        { label: 'Opened', value: formatNumber(totals.opened) },
        { label: 'Clicked', value: formatNumber(totals.clicked) },
        { label: 'Gifts', value: formatNumber(totals.gifts) },
        { label: 'Revenue', value: formatCurrency(totals.revenue) }
    ]), [rows.length, totals]);

    return (
        <Box sx={{ display: 'grid', gap: 2 }}>

            <Card variant="outlined">
                <CardContent sx={{ p: 0 }}>
                    <Tabs
                        value={value}
                        onChange={(_, nextValue) => setValue(nextValue)}
                        variant="scrollable"
                        scrollButtons="auto"
                    >
                        <Tab label="Campaign" {...a11yProps(0)} />
                        <Tab label="Notes" {...a11yProps(1)} />
                    </Tabs>
                </CardContent>
            </Card>

            <ReportLeadCard eyebrow="Virtuous" title="From delivery to donor response" description="Review email delivery, clicks, gifts, and recorded revenue." />

            <TabPanel value={value} index={0}>
                {loading ? (
                    <GlassLoadingState
                        icon={<MdAnalytics size={34} />}
                        title={`Loading ${title}`}
                        subtitle={dateRangeLabel ? `Pulling delivered, opened, clicked, and donor response activity for ${dateRangeLabel}.` : 'Pulling delivered, opened, clicked, and donor response activity for the selected range.'}
                    />
                ) : rows.length > 0 ? (
                    <Box sx={{ display: 'grid', gap: 2 }}>
                        <Box
                            sx={{
                                display: 'grid',
                                gap: 1,
                                gridTemplateColumns: {
                                    xs: 'repeat(2, minmax(0, 1fr))',
                                    md: 'repeat(3, minmax(0, 1fr))',
                                    xl: 'repeat(6, minmax(0, 1fr))'
                                }
                            }}
                        >
                            {kpiItems.map((item) => (
                                <Card key={item.label} variant="outlined">
                                    <CardContent>
                                        <Typography variant="caption" color="text.secondary">
                                            {item.label}
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                            {item.value}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            ))}
                        </Box>

                        <Card variant="outlined">
                            <CardContent sx={{ p: 0 }}>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 700 }}>Email Name</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700 }}>Delivered</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700 }}>Opened</TableCell>
                                                <TableCell data-report-metric="click" align="right" sx={{ fontWeight: 700 }}>Clicked</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700 }}>Bounces</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700 }}>Unsubscribes</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700 }}>Gifts</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700 }}>Revenue</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {rows.map((row, index) => (
                                                <TableRow
                                                    key={row.id}
                                                    sx={{
                                                        '&:nth-of-type(odd)': {
                                                            backgroundColor: alpha(theme.palette.primary.main, 0.03)
                                                        }
                                                    }}
                                                >
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                            {row.emailName}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <NumericFormat value={row.delivered} displayType="text" thousandSeparator="," />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <NumericFormat value={row.opened} displayType="text" thousandSeparator="," />
                                                    </TableCell>
                                                    <TableCell data-report-metric="click" align="right">
                                                        <NumericFormat value={row.clicked} displayType="text" thousandSeparator="," />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <NumericFormat value={row.bounced} displayType="text" thousandSeparator="," />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <NumericFormat value={row.unsubscribe} displayType="text" thousandSeparator="," />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <NumericFormat value={row.gifts} displayType="text" thousandSeparator="," />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {formatCurrency(row.revenue)}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            <TableRow data-report-total="true">
                                                <TableCell sx={{ fontWeight: 700 }}>Totals</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700 }}>{formatNumber(totals.delivered)}</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700 }}>{formatNumber(totals.opened)}</TableCell>
                                                <TableCell data-report-metric="click" align="right" sx={{ fontWeight: 700 }}>{formatNumber(totals.clicked)}</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700 }}>{formatNumber(totals.bounced)}</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700 }}>{formatNumber(totals.unsubscribe)}</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700 }}>{formatNumber(totals.gifts)}</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(totals.revenue)}</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </CardContent>
                        </Card>
                    </Box>
                ) : (
                    <Card variant="outlined">
                        <CardContent>
                            <Typography align="center">No {title} data found for the selected date range.</Typography>
                        </CardContent>
                    </Card>
                )}
            </TabPanel>

            <TabPanel value={value} index={1}>
                <Comments
                    eDate={sessionStorage.getItem('eDate')}
                    sDate={sessionStorage.getItem('sDate')}
                    color={theme.palette.primary.main}
                    admintoken={0}
                    view="a7edd5c0-7a77-49ed-86ad-6fe3066a0f59"
                    type="virtuous"
                    setLoading={props?.setLoading || (() => {})}
                />
            </TabPanel>
        </Box>
    );
};

export default VirtuousReport;
